import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../data/products";
import { MaskedLine } from "./Reveal";
import { useLang } from "../i18n";

export const Hero = ({ onNavigate }) => {
    const { t } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section ref={ref} id="top" className="relative h-screen min-h-[640px] overflow-hidden" data-testid="hero-section">
            <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
                <img
                    src={IMAGES.heroArt}
                    alt="Eiffel Tower amid ice crystals — the world of French Ice"
                    className="w-full h-full object-cover object-center"
                    data-testid="hero-artwork"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-ivory/70 via-ivory/20 to-ivory" />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
                <motion.p
                    initial={{ opacity: 0, letterSpacing: "0.6em" }}
                    animate={{ opacity: 1, letterSpacing: "0.35em" }}
                    transition={{ duration: 1.4, delay: 0.3 }}
                    className="text-xs sm:text-sm uppercase text-golddark mb-8"
                    data-testid="hero-overline"
                >
                    {t("hero.overline")}
                </motion.p>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-charcoal" data-testid="hero-headline">
                    <MaskedLine delay={0.5}>{t("hero.l1")}</MaskedLine>
                    <MaskedLine delay={0.65}><em className="text-golddark">{t("hero.l2")}</em></MaskedLine>
                    <MaskedLine delay={0.8}>{t("hero.l3")}</MaskedLine>
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.15 }}
                    className="mt-8 max-w-md text-sm sm:text-base text-smoke leading-relaxed"
                    data-testid="hero-subtext"
                >
                    {t("hero.sub")}
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.35 }}
                    className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                    <button
                        data-testid="hero-explore-button"
                        onClick={() => onNavigate("french-ice")}
                        className="bg-charcoal text-goldlight px-10 py-4 text-xs tracking-[0.25em] uppercase hover:bg-golddark hover:text-ivory transition-colors duration-300"
                    >
                        {t("hero.explore")}
                    </button>
                    <button
                        data-testid="hero-story-button"
                        onClick={() => onNavigate("story")}
                        className="border border-gold text-golddark px-10 py-4 text-xs tracking-[0.25em] uppercase hover:bg-gold hover:text-ivory transition-colors duration-300"
                    >
                        {t("hero.story")}
                    </button>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
            >
                <span className="text-[10px] tracking-[0.3em] uppercase text-ash">{t("hero.scroll")}</span>
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                    className="block w-px h-10 bg-gold"
                />
            </motion.div>
        </section>
    );
};
